/* eslint-disable indent */
import { IAsk } from '@/types'
import { getAsk } from 'api/asks'
import { getBid } from 'api/bids'
import { ArtistsNames } from 'components/artists/ArtistsNames'
import { ArtworkTags } from 'components/artwork/ArtworkTags'
import { ArtworkDetails } from 'components/artwork/ArtworksDetails'
import { Info } from 'components/common/Info'
import { Price } from 'components/common/Price'
import { ReviewBanner } from 'components/common/ReviewBanner'
import { ToggleButton } from 'components/common/ToggleButton'
import { TransactionNotCompletedModal } from 'components/common/TransactionNotCompletedModal'
import { useWaysTo } from 'components/common/WaysTo'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import { useFeatureFlags, useUserProfile } from 'hooks'
import { useExistingTrade } from 'hooks/useExistingTrade'
import { useUserFees } from 'hooks/useUserFees'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ComponentProps, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { TradeType } from 'types'
import { pickPrice, pickTradePrice } from 'utils/currencies'
import { Artwork, Edition } from 'utils/data'
import { getFeesFromAskAndBid } from 'utils/fees'
import { omit } from 'utils/objects'
import { getEditionShortName } from 'utils/strings'
import { getArtworkUrlFromRoot } from 'utils/urls'
import routes from '../../constants/routes'
import { useOverlay } from '../../contexts'

const AskForm = dynamic(
  () => import('components/forms/AskForm').then(m => m.AskForm),
  { ssr: false }
)
const BidForm = dynamic(
  () => import('components/forms/BidForm').then(m => m.BidForm),
  { ssr: false }
)
const BuyForm = dynamic(
  () => import('components/forms/BuyForm').then(m => m.BuyForm),
  { ssr: false }
)
const SellForm = dynamic(
  () => import('components/forms/SellForm').then(m => m.SellForm),
  { ssr: false }
)

type Props = {
  type?: 'ask-or-sell' | 'bid-or-buy'
  artwork: Artwork
  currentEdition: Edition
}

export const ArtworkTradeForm = ({
  type = 'ask-or-sell',
  artwork,
  currentEdition,
}: Props) => {
  const { t } = useTranslation('common')
  const setWaysTo = useWaysTo()
  const router = useRouter()
  const { query } = router
  const { action } = router.query
  const bidId = Number(query?.bidId) || undefined
  const askId = Number(query?.askId) || undefined
  const price = Number(query?.price) || 0
  const { setConfig, setIsOpen } = useOverlay()
  const { featureFlags } = useFeatureFlags()

  const [displayNextStepMessage, setDisplayNextStepMessage] = useState(false)
  const [hasShippingAndBilling, setHasShippingAndBilling] = useState(false)
  const [active, setActive] = useState<TradeType>(() => {
    if (action === 'buy' && currentEdition?.lowestAsk) return 'buy'
    if (action === 'sell' && currentEdition?.highestBid) return 'sell'

    if (type === 'ask-or-sell') return 'ask'
    else return 'bid'
  })
  const { userCurrency } = useCurrencies()
  const useShipStation = featureFlags['use-shipstation-for-shipping']

  const [artworkDetails, setArtworkDetails] =
    useState<ComponentProps<typeof ArtworkDetails>>()
  const [missedBuy, setMissedBuy] = useState<{
    tries: number
    bidStatus: string | undefined
  }>({
    tries: 0,
    bidStatus: undefined,
  })

  const { data: userProfile } = useUserProfile()

  const { name, artists, id } = artwork

  const getObject = (obj: ComponentProps<typeof ArtworkDetails>) => {
    if (!obj) return false
    if (!obj?.includes) return false
    const includes = obj?.includes
    if (includes) {
      const includesWithoutId = omit(includes, ['id'])
      return Object.values(includesWithoutId).some(Boolean)
    }
  }

  const hasIncludes = getObject(currentEdition?.artworkDetails)

  const { data: ask } = useQuery(
    ['ask', askId, action],
    async () => {
      if (!askId || action !== 'edit') return

      const { data } = await getAsk(askId)

      return data
    },
    { keepPreviousData: true }
  )

  const { data: bid } = useQuery(
    ['bid', bidId, action],
    async () => {
      if (!bidId || action !== 'edit') return

      const { data } = await getBid(bidId)

      return data
    },
    { keepPreviousData: true }
  )

  const { data: hasMissedBid } = useQuery(
    ['missed-bid', bidId, action],
    async () => {
      if (!bidId || action !== 'buy') return

      const { data } = await getBid(bidId)

      const status = data?.attributes?.status

      setMissedBuy(prevState => ({
        ...prevState,
        tries: prevState.tries + 1,
        bidStatus: status,
      }))

      if (missedBuy.tries < 3 && status !== 'MissedOut') return undefined

      return status === 'MissedOut'
    },
    {
      keepPreviousData: false,
      refetchInterval: 1000,
      enabled: Boolean(
        bidId &&
          action === 'buy' &&
          missedBuy.tries <= 3 &&
          missedBuy.bidStatus !== 'MissedOut'
      ),
    }
  )

  const existingTradePayload = {
    currentEdition,
    artists,
    name,
    id,
  }

  const { existingTrade: existingAsk, editUrl: editAskUrl } = useExistingTrade({
    ...existingTradePayload,
    tradeType: 'ask',
  })
  const { existingTrade: existingBid, editUrl: editBidUrl } = useExistingTrade({
    ...existingTradePayload,
    tradeType: 'bid',
  })

  useEffect(() => {
    setArtworkDetails(currentEdition.artworkDetails)
  }, [currentEdition.artworkDetails])

  useEffect(() => {
    if (!userProfile || !active) return

    const address =
      active === 'sell' || active === 'ask'
        ? userProfile?.senderAddress
        : userProfile?.shippingAddress

    if (active === 'sell' || active === 'ask') {
      if (useShipStation) {
        if (address?.country !== 'GB' && address?.country !== 'US')
          setHasShippingAndBilling(false)
      } else {
        if (address?.country !== 'GB') setHasShippingAndBilling(false)
      }

      setHasShippingAndBilling(
        Boolean(
          address && userProfile?.billingAddress && userProfile?.buyingCard
        )
      )
    } else setHasShippingAndBilling(Boolean(address))
  }, [active, userProfile])

  useEffect(() => {
    if (typeof hasMissedBid === 'undefined') return

    const redirect = () => {
      router.push({ query: { slug: router.query.slug } }, undefined, {
        shallow: true,
      })
    }

    if (hasMissedBid) {
      setConfig({
        body: <TransactionNotCompletedModal type='buy' price={price} />,
        className: 'items-center p-6',
        onClose: redirect,
      })
      setIsOpen(true)
      return
    }

    redirect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMissedBid])

  const { fees: userFees, isGB } = useUserFees()

  const initValues = () => {
    return {
      bid: bid?.attributes
        ? (pickPrice({
            price: bid.attributes.price,
            priceCurrency: bid.attributes.currency,
            priceGbp: bid.attributes.priceConvertedToGbp,
            priceUsd: bid.attributes.priceConvertedToUsd,
            priceEur: bid.attributes.priceConvertedToEur,
            userCurrency,
          }) || 0) / 100
        : 0,
      ask: ask?.attributes
        ? (pickPrice({
            price: ask.attributes.price,
            priceCurrency: ask.attributes.currency,
            priceGbp: ask.attributes.priceConvertedToGbp,
            priceUsd: ask.attributes.priceConvertedToUsd,
            priceEur: ask.attributes.priceConvertedToEur,
            userCurrency,
          }) || 0) / 100
        : 0,
    }
  }

  const [values, setValues] = useState(() => initValues())

  const fees =
    action === 'sell' && existingAsk
      ? getFeesFromAskAndBid(existingAsk as IAsk['attributes'])
      : action === 'edit'
      ? getFeesFromAskAndBid(ask?.attributes, bid?.attributes)
      : userFees

  return (
    <>
      <ArtistsNames
        artists={artists}
        clickable={false}
        className='hidden lg:flex'
      />
      <h4 className='mb-1 hidden font-medium text-cool-900 lg:block'>
        {name}{' '}
        {getEditionShortName(currentEdition?.name, currentEdition.otherName)}
      </h4>
      <div
        className={`hidden flex-col space-y-1 lg:flex ${
          type === 'bid-or-buy' ? 'mb-3' : 'mb-6'
        } `}
      >
        <div className='flex space-x-2.5 text-sm text-cool-600'>
          <span>
            {t('highest_bid')}:{' '}
            <Price
              data-e2e='highest-bid-value'
              formattingOptions={{ from: userCurrency, to: userCurrency }}
            >
              {(pickTradePrice(userCurrency, currentEdition.highestBid) || 0) /
                100}
            </Price>
          </span>
          <span className='text-cool-400'>|</span>
          <span>
            {t('lowest_ask')}:{' '}
            <Price
              data-e2e='lowest-ask-value'
              formattingOptions={{ from: userCurrency, to: userCurrency }}
            >
              {(pickTradePrice(userCurrency, currentEdition.lowestAsk) || 0) /
                100}
            </Price>
          </span>
          <span className='text-cool-400'>|</span>
          <button
            className='text-secondary-default underline'
            onClick={() => setWaysTo(type === 'bid-or-buy' ? 'buy' : 'sell')}
          >
            {type === 'bid-or-buy'
              ? t('how_buying_works')
              : t('how_selling_works')}
          </button>
        </div>
        {type === 'ask-or-sell' && !artworkDetails?.features?.framed && (
          <Link
            href={routes.intercom.canISellFramed}
            target='_blank'
            rel='noreferrer'
            className='text-xs text-secondary-default underline'
          >
            {t('what_if_my_artwork_is_framed')}
          </Link>
        )}
      </div>
      {type === 'bid-or-buy' && (
        <>
          <div className='flex justify-center lg:justify-start'>
            <ArtworkTags
              currentEdition={currentEdition}
              isVerified
              isNewCondition
              showAdditionalVerification={false}
              keepTags
              tagSize='S'
              tagsContentClassName='!mb-6'
            />
          </div>

          {existingBid && query.action === 'edit' && (
            <div className='px-6 md:p-0'>
              <Info className='mb-6' theme='primary-light'>
                {t('buy_or_bid:you_already_have_a_live_offer')}
              </Info>
            </div>
          )}
        </>
      )}
      {type === 'ask-or-sell' && (
        <>
          {existingAsk && query.action === 'edit' && (
            <div className='px-6 md:p-0'>
              <Info className='mb-6' theme='primary-light'>
                <Trans
                  i18nKey='ask_or_sell:you_already_have_a_live_listing'
                  components={{
                    link: (
                      <Link
                        href={`${getArtworkUrlFromRoot(
                          'ask-or-sell',
                          artists,
                          name,
                          id
                        )}/${currentEdition.name}?action=ask`}
                        className='text-secondary-default underline'
                      />
                    ),
                  }}
                />
              </Info>
            </div>
          )}

          {hasIncludes && query.action !== 'edit' && (
            <div className='px-6 md:p-0'>
              <Info className='mb-5'>
                <span>{t('single_artwork:this_artwork_must_include')}</span>{' '}
                <div className='mt-[5px]'>
                  <ArtworkDetails
                    includes={artworkDetails?.includes}
                    hideTitle
                  />
                </div>
              </Info>
            </div>
          )}
        </>
      )}

      <div className='mb-4 lg:mb-5'>
        <div className='hidden lg:block'>
          <ReviewBanner size='md' />
        </div>
        <div className='lg:hidden'>
          <ReviewBanner size='sm' />
        </div>
      </div>

      <div className='rounded-t border border-b-0 border-solid border-cool-300 bg-cool-50 p-6 pb-0 lg:p-8 lg:pb-0'>
        <div className='rounded rounded-b-none border border-b-0 border-cool-300 bg-warm-50 p-5 pb-0 lg:border-none lg:bg-transparent lg:p-0'>
          {type === 'ask-or-sell' && (
            <ToggleButton
              leftState={{
                value: 'ask',
                label: t('ask_or_sell:place_ask'),
              }}
              rightState={{
                value: 'sell',
                label: t('ask_or_sell:sell_now'),
                disabled: !currentEdition.highestBid,
                tooltipText: !currentEdition.highestBid
                  ? t('ask_or_sell:currently_no_active_bids')
                  : null,
              }}
              onChange={value => {
                setActive(value as TradeType)
                if (value === 'ask' && editAskUrl) {
                  router.replace(editAskUrl)
                } else {
                  router.replace(
                    `${getArtworkUrlFromRoot(
                      'ask-or-sell',
                      artists,
                      name,
                      id
                    )}/${currentEdition.name}?action=${value}`
                  )
                }
              }}
              danger
              value={
                currentEdition.highestBid &&
                action !== 'edit' &&
                action !== 'ask'
                  ? 'sell'
                  : 'ask'
              }
              activeSell={active === 'sell'}
            />
          )}
          {type === 'bid-or-buy' && (
            <ToggleButton
              leftState={{
                value: 'bid',
                label: t('common:make_Offer'),
                tooltipText:
                  active === 'bid' && !values.bid
                    ? t('buy_or_bid:please_enter_an_offer_price_below')
                    : null,
              }}
              rightState={{
                value: 'buy',
                label: t('buy_or_bid:buy_now'),
                disabled: !currentEdition.lowestAsk,
                tooltipText: !currentEdition.lowestAsk
                  ? t('buy_or_bid:currently_no_active_listings')
                  : null,
              }}
              onChange={value => {
                setActive(value as TradeType)
                if (value === 'bid') {
                  if (editBidUrl) router.push(editBidUrl)
                  else if (values.bid) setDisplayNextStepMessage(true)
                } else {
                  setValues(initValues())
                  router.replace(
                    `${getArtworkUrlFromRoot(
                      'bid-or-buy',
                      artists,
                      name,
                      id
                    )}/${currentEdition.name}?action=${value}`
                  )
                }
              }}
              value={
                currentEdition.lowestAsk &&
                action !== 'edit' &&
                action !== 'bid'
                  ? 'buy'
                  : 'bid'
              }
              activeBuy={active === 'buy'}
            />
          )}
        </div>

        {displayNextStepMessage && Boolean(values.bid) && active === 'bid' && (
          <NextStepMessage hasShippingAndBilling={hasShippingAndBilling} />
        )}
      </div>

      {active === 'ask' && (
        <AskForm
          artwork={artwork}
          currentEdition={currentEdition}
          hasShippingAndBilling={hasShippingAndBilling}
          defaultAsk={values.ask}
          setActiveSell={() => setActive('sell')}
          fees={fees}
          active={active}
          fromArtwork={query.fromArtwork as unknown as boolean}
        />
      )}
      {active === 'sell' && (
        <SellForm
          artwork={artwork}
          currentEdition={currentEdition}
          hasShippingAndBilling={hasShippingAndBilling}
          fees={fees}
        />
      )}
      {active === 'bid' && (
        <BidForm
          artwork={artwork}
          currentEdition={currentEdition}
          defaultBid={values.bid}
          onChange={value => setValues(prev => ({ ...prev, bid: value }))}
          setActiveBuy={() => setActive('buy')}
          hasShippingAndBilling={hasShippingAndBilling}
          fees={fees}
          isGB={isGB}
          fromArtwork={query.fromArtwork as unknown as boolean}
        />
      )}
      {active === 'buy' && (
        <BuyForm
          artwork={artwork}
          currentEdition={currentEdition}
          hasShippingAndBilling={hasShippingAndBilling}
          fees={fees}
          isGB={isGB}
        />
      )}
    </>
  )
}

interface NextStepMessageProps {
  hasShippingAndBilling?: boolean
}

function NextStepMessage({ hasShippingAndBilling }: NextStepMessageProps) {
  const { t } = useTranslation()

  const onClick = () => {
    const bidForm = document.getElementById('bid-form') as HTMLFormElement
    bidForm?.requestSubmit()
  }

  return (
    <div className='mt-5 rounded bg-primary-light px-4 py-2 text-center'>
      <p className='text-sm font-medium'>
        <Trans
          i18nKey='buy_or_bid:ready_to_proceed_continue_to_next_step'
          values={{
            nextStep: hasShippingAndBilling
              ? t('buy_or_bid:review_bid')
              : t('buy_or_bid:shipping'),
          }}
          components={{
            button: (
              <button
                type='button'
                className='text-purple-600 underline'
                onClick={onClick}
              />
            ),
          }}
        />
      </p>
    </div>
  )
}
